import {
  Pushbutton,
  Pushtext,
  UiconClipboard,
  UiconKeyHorizontal
} from '@stratumn/atomic';
import { DownArrowFill } from '@stratumn/icons';
import moment from 'moment';
import PropType from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import injectSheet from 'react-jss';
import addToClipBoard from 'utils/addToClipBoard';
import pluralize from 'utils/pluralize';

import styles from './keys.style';

// split active and expired keys
const splitKeys = keys => {
  const activeKeys = [];
  const expiredKeys = [];
  const nbKeys = keys.length;
  for (let idx = 0; idx < nbKeys; idx += 1) {
    const key = keys[idx];
    if (key.deprecated) {
      expiredKeys.unshift({
        idx,
        key
      });
    } else {
      activeKeys.unshift({
        idx,
        key
      });
    }
  }

  return { activeKeys, expiredKeys };
};

export const Keys = ({ classes, keys, expireKey, addKey }) => {
  const [copiedPublicKey, setCopiedPublicKey] = useState(null);
  const [displayExpiredKeys, setDisplayExpiredKeys] = useState(false);
  const pkTimeoutRef = useRef(null);

  // clear copied key timeout on unmount
  useEffect(
    () => () => {
      if (pkTimeoutRef.current) {
        clearTimeout(pkTimeoutRef.current);
      }
    },
    []
  );

  // split active and expired keys
  const { activeKeys, expiredKeys } = useMemo(() => splitKeys(keys), [keys]);

  // copy the publicKey form the key object provided and set component state accordingly
  // timeout after 1500ms and release the state of the component
  const copyPublicKey = useCallback(async key => {
    const { rowId, publicKey } = key;
    addToClipBoard(publicKey);
    setCopiedPublicKey(rowId);
    if (pkTimeoutRef.current) {
      clearTimeout(pkTimeoutRef.current);
    }
    pkTimeoutRef.current = setTimeout(() => {
      setCopiedPublicKey(null);
    }, 1500);
  }, []);

  // active keys
  const activeKeysSection = (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionHeaderTitle}>
          <div className={classes.sectionHeaderTitleText}>Signing Keys</div>
          <UiconKeyHorizontal size={27} />
        </div>
      </div>
      {activeKeys.map(({ idx, key }) => {
        const { createdAt, rowId } = key;
        const isCopiedKey = copiedPublicKey === rowId;
        const keyName = `#${idx < 9 ? '0' : ''}${idx + 1}`;
        return (
          <div className={classes.keyItem} key={rowId}>
            <div className={classes.keyInfo}>
              <div className={classes.keyIdx}>{keyName}</div>
              <div className={classes.keyEvent}>{`Created: ${moment(
                createdAt
              ).format('DD MMM YYYY')}`}</div>
            </div>
            <div className={classes.keyActions}>
              {activeKeys.length > 1 && (
                <div className={classes.keyAction}>
                  <Pushtext
                    data-testid="expire-key"
                    onClick={() =>
                      expireKey({
                        name: keyName,
                        key
                      })
                    }
                  >
                    Expire
                  </Pushtext>
                </div>
              )}
              <div className={classes.keyAction}>
                <Pushtext
                  data-testid="copy-key"
                  mute={!isCopiedKey}
                  onClick={() => copyPublicKey(key)}
                  suffix={<UiconClipboard checked={isCopiedKey} size={22} />}
                >
                  {`${isCopiedKey ? 'Copied' : 'Copy'} public key`}
                </Pushtext>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // expired keys
  const isCollapsible = expiredKeys.length > 0;
  const expiredKeysSection = (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionHeaderTitle}>
          <div className={classes.sectionHeaderTitleText}>Expired Keys</div>
          <UiconKeyHorizontal size={27} />
        </div>
        <div
          className={classes.sectionHeaderCollapse}
          data-is-collapsible={isCollapsible}
          data-testid="display-expired-keys"
          onClick={() =>
            isCollapsible && setDisplayExpiredKeys(!displayExpiredKeys)
          }
        >
          <div className={classes.sectionHeaderCollapseMessage}>
            {pluralize('expired key', expiredKeys.length)}
          </div>
          {isCollapsible && (
            <DownArrowFill
              className={classes.sectionHeaderCollapseIcon}
              data-input-open={displayExpiredKeys}
            />
          )}
        </div>
      </div>
      {displayExpiredKeys &&
        expiredKeys.map(({ idx, key }) => {
          const { createdAt, deprecatedAt, rowId } = key;
          const isCopiedKey = copiedPublicKey === rowId;
          return (
            <div className={classes.keyItem} key={rowId}>
              <div className={classes.keyInfo}>
                <div className={classes.keyIdx}>{`#${idx < 9 ? '0' : ''}${
                  idx + 1
                }`}</div>
                <div className={classes.keyEvent}>{`Created: ${moment(
                  createdAt
                ).format('DD MMM YYYY')}`}</div>
                <div className={classes.keyEvent}>{`Expired: ${moment(
                  deprecatedAt
                ).format('DD MMM YYYY')}`}</div>
              </div>
              <div className={classes.keyActions}>
                <div className={classes.keyAction}>
                  <Pushtext
                    data-testid="copy-key"
                    mute={!isCopiedKey}
                    onClick={() => copyPublicKey(key)}
                    suffix={<UiconClipboard checked={isCopiedKey} size={22} />}
                  >
                    {`${isCopiedKey ? 'Copied' : 'Copy'} public key`}
                  </Pushtext>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );

  // add key button
  const addKeyButton = (
    <div className={classes.addKeyButton}>
      <Pushbutton data-testid="add-key" onClick={addKey} secondary>
        Add a signing key
      </Pushbutton>
    </div>
  );

  // render
  return (
    <>
      {activeKeysSection}
      {addKeyButton}
      {expiredKeysSection}
    </>
  );
};

Keys.propTypes = {
  classes: PropType.object.isRequired,
  keys: PropType.arrayOf(PropType.object).isRequired,
  expireKey: PropType.func.isRequired,
  addKey: PropType.func.isRequired
};

export default injectSheet(styles)(React.memo(Keys));
